import type {
  Member,
  NewsArticle,
  Benefit,
  Schedule,
  ScheduleType,
  AssetItem,
  AssetFolder,
  ApiResponse,
  PaginationMeta,
  DiscographyRelease,
  ChekiPricing,
  ChekiOrder,
} from '@tsuyosa/types';

const API_URL = import.meta.env.VITE_API_URL ?? '';

async function authFetch<T>(
  path: string,
  token: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    const err = !data.success ? data.error : { code: 'UNKNOWN', message: 'Request failed' };
    throw new Error(typeof err === 'string' ? err : err.message);
  }
  return (data as { success: true; data: T }).data;
}

async function authFetchPaginated<T>(
  path: string,
  token: string,
): Promise<{ data: T; meta: Partial<PaginationMeta> }> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<T> = await res.json();
  if (!res.ok || !json.success) {
    const err = !json.success ? json.error : { message: 'Request failed' };
    throw new Error(typeof err === 'string' ? err : err.message);
  }
  return { data: (json as { success: true; data: T }).data, meta: json.meta ?? {} };
}

// ─── Members ────────────────────────────────────────────────────────────────

export async function getAdminMembers(
  token: string,
): Promise<{ data: Member[]; meta: Partial<PaginationMeta> }> {
  return authFetchPaginated<Member[]>('/api/v1/admin/members', token);
}

export async function createMember(token: string, body: Partial<Member>): Promise<Member> {
  return authFetch<Member>('/api/v1/admin/members', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateMember(
  token: string,
  id: string,
  body: Partial<Member>,
): Promise<Member> {
  return authFetch<Member>(`/api/v1/admin/members/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteMember(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/members/${id}`, token, { method: 'DELETE' });
}

// ─── News ────────────────────────────────────────────────────────────────────

export async function getAdminNews(
  token: string,
): Promise<{ data: NewsArticle[]; meta: Partial<PaginationMeta> }> {
  return authFetchPaginated<NewsArticle[]>('/api/v1/admin/news', token);
}

export async function createNews(
  token: string,
  body: Partial<NewsArticle>,
): Promise<NewsArticle> {
  return authFetch<NewsArticle>('/api/v1/admin/news', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateNews(
  token: string,
  id: string,
  body: Partial<NewsArticle>,
): Promise<NewsArticle> {
  return authFetch<NewsArticle>(`/api/v1/admin/news/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteNews(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/news/${id}`, token, { method: 'DELETE' });
}

// ─── Benefits ────────────────────────────────────────────────────────────────

export async function getAdminBenefits(token: string): Promise<Benefit[]> {
  return authFetch<Benefit[]>('/api/v1/admin/benefits', token);
}

export async function createBenefit(token: string, body: Partial<Benefit>): Promise<Benefit> {
  return authFetch<Benefit>('/api/v1/admin/benefits', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateBenefit(token: string, id: string, body: Partial<Benefit>): Promise<Benefit> {
  return authFetch<Benefit>(`/api/v1/admin/benefits/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteBenefit(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/benefits/${id}`, token, { method: 'DELETE' });
}

// ─── Upload ──────────────────────────────────────────────────────────────────

export async function deleteFile(token: string, fileId: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/upload/${fileId}`, token, { method: 'DELETE' });
}

// ─── Schedule Types ──────────────────────────────────────────────────────────

export async function getAdminScheduleTypes(token: string): Promise<ScheduleType[]> {
  return authFetch<ScheduleType[]>('/api/v1/admin/schedules/types', token);
}

export async function createScheduleType(
  token: string,
  body: Pick<ScheduleType, 'name_id' | 'name_jp' | 'sort_order' | 'is_active'>,
): Promise<ScheduleType> {
  return authFetch<ScheduleType>('/api/v1/admin/schedules/types', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateScheduleType(
  token: string,
  id: string,
  body: Partial<Pick<ScheduleType, 'name_id' | 'name_jp' | 'sort_order' | 'is_active'>>,
): Promise<ScheduleType> {
  return authFetch<ScheduleType>(`/api/v1/admin/schedules/types/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteScheduleType(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/schedules/types/${id}`, token, { method: 'DELETE' });
}

// ─── Schedules ───────────────────────────────────────────────────────────────

export async function getAdminSchedules(token: string): Promise<Schedule[]> {
  return authFetch<Schedule[]>('/api/v1/admin/schedules', token);
}

export async function getAdminScheduleById(token: string, id: string): Promise<Schedule> {
  return authFetch<Schedule>(`/api/v1/admin/schedules/${id}`, token);
}

export async function createSchedule(
  token: string,
  body: Omit<Partial<Schedule>, 'members'> & { members?: { member_id: string; sort_order: number }[] },
): Promise<Schedule> {
  return authFetch<Schedule>('/api/v1/admin/schedules', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateSchedule(
  token: string,
  id: string,
  body: Omit<Partial<Schedule>, 'members'> & { members?: { member_id: string; sort_order: number }[] },
): Promise<Schedule> {
  return authFetch<Schedule>(`/api/v1/admin/schedules/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteSchedule(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/schedules/${id}`, token, { method: 'DELETE' });
}

// ─── Assets ──────────────────────────────────────────────────────────────────

export async function getAssets(token: string, folder?: AssetFolder): Promise<AssetItem[]> {
  const params = folder ? `?folder=${folder}` : '';
  const res = await fetch(`${API_URL}/api/v1/admin/assets${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json: ApiResponse<AssetItem[]> = await res.json();
  if (!json.success) throw new Error(typeof json.error === 'string' ? json.error : json.error.message);
  return json.data;
}

export async function deleteAsset(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/assets/${id}`, token, { method: 'DELETE' });
}

export async function uploadAsset(
  token: string,
  blob: Blob,
  filename: string,
  feature: AssetFolder,
  width: number,
  height: number
): Promise<AssetItem> {
  const formData = new FormData();
  formData.append('file', blob, filename);
  formData.append('feature', feature);
  formData.append('width', String(width));
  formData.append('height', String(height));
  const res = await fetch(`${API_URL}/api/v1/admin/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json: ApiResponse<AssetItem> = await res.json();
  if (!res.ok || !json.success) {
    const err = !json.success ? json.error : { message: 'Upload failed' };
    throw new Error(typeof err === 'string' ? err : err.message);
  }
  return (json as { success: true; data: AssetItem }).data;
}

// ─── Discography ─────────────────────────────────────────────────────────────

export async function getAdminDiscography(token: string): Promise<DiscographyRelease[]> {
  return authFetch<DiscographyRelease[]>('/api/v1/admin/discography', token);
}

export async function getAdminReleaseById(token: string, id: string): Promise<DiscographyRelease> {
  return authFetch<DiscographyRelease>(`/api/v1/admin/discography/${id}`, token);
}

export interface ReleasePayload {
  slug: string;
  title: string;
  type: string;
  cover_url?: string;
  release_date?: string;
  date_precision?: string;
  streaming_links?: Record<string, string>;
  description?: string;
  is_published?: boolean;
  order_index?: number;
  members?: { member_id: string; role_in_release?: string; sort_order?: number }[];
}

export async function createRelease(token: string, body: ReleasePayload): Promise<DiscographyRelease> {
  return authFetch<DiscographyRelease>('/api/v1/admin/discography', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateRelease(token: string, id: string, body: Partial<ReleasePayload>): Promise<DiscographyRelease> {
  return authFetch<DiscographyRelease>(`/api/v1/admin/discography/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteRelease(token: string, id: string): Promise<void> {
  await authFetch<void>(`/api/v1/admin/discography/${id}`, token, { method: 'DELETE' });
}

// ─── Cheki ───────────────────────────────────────────────────────────────────

export interface ScheduleChekiData {
  cheki_enabled: boolean;
  pricing: ChekiPricing[];
}

export async function getScheduleCheki(id: string): Promise<ScheduleChekiData> {
  const res = await fetch(`${API_URL}/api/v1/schedules/${id}/cheki`);
  const json: ApiResponse<ScheduleChekiData> = await res.json();
  if (!json.success) throw new Error('Failed to load cheki data');
  return (json as { success: true; data: ScheduleChekiData }).data;
}

export interface ChekiPricingRow {
  type: '2shot_solo' | 'all_member';
  price_preorder: number;
  price_onspot: number;
}

export async function updateChekiPricing(
  token: string,
  scheduleId: string,
  rows: ChekiPricingRow[],
): Promise<{ pricing: ChekiPricing[] }> {
  return authFetch<{ pricing: ChekiPricing[] }>(
    `/api/v1/admin/schedules/${scheduleId}/cheki-pricing`,
    token,
    { method: 'PUT', body: JSON.stringify(rows) },
  );
}

export async function getAdminChekiOrders(
  token: string,
  options?: { schedule_id?: string; status?: string },
): Promise<ChekiOrder[]> {
  const params = new URLSearchParams();
  if (options?.schedule_id) params.set('schedule_id', options.schedule_id);
  if (options?.status)      params.set('status', options.status);
  const q = params.toString() ? `?${params.toString()}` : '';
  return authFetch<ChekiOrder[]>(`/api/v1/admin/cheki/orders${q}`, token);
}

export async function updateChekiOrderStatus(
  token: string,
  orderId: string,
  status: string,
): Promise<ChekiOrder> {
  return authFetch<ChekiOrder>(`/api/v1/admin/cheki/orders/${orderId}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}
